/**
 * Invite Team User Dialog
 *
 * Asks for an email + role and submits to /v1/admin/dashboard/team/invite.
 * Managers can only invite members; the role selector hides admin/manager
 * options for them.
 */

import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useInviteTeamUser } from '@/hooks/use-team';
import type { TeamRole } from '@/types/team';

interface InviteTeamUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callerRole: TeamRole;
  onInvited?: (email: string) => void;
}

function allowedInviteRoles(callerRole: TeamRole): TeamRole[] {
  if (callerRole === 'admin') return ['admin', 'manager', 'member'];
  if (callerRole === 'manager') return ['member'];
  return [];
}

export function InviteTeamUserDialog({
  open,
  onOpenChange,
  callerRole,
  onInvited,
}: InviteTeamUserDialogProps) {
  const roleOptions = allowedInviteRoles(callerRole);
  const defaultRole: TeamRole = roleOptions[0] ?? 'member';

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>(defaultRole);

  const invite = useInviteTeamUser();

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Reset form on close so the next open starts clean.
      setEmail('');
      setRole(defaultRole);
      invite.reset();
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      await invite.mutateAsync({ email: trimmed, role });
      onInvited?.(trimmed);
      handleOpenChange(false);
    } catch {
      // Mutation error surfaces via `invite.error` below.
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Invite a team member</DialogTitle>
            <DialogDescription>
              Cognito will email them a temporary password. They&apos;ll be prompted to set a
              new one on first sign-in.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="name@company.com"
                required
                autoFocus
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={invite.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={role}
                onValueChange={value => setRole(value as TeamRole)}
                disabled={invite.isPending || roleOptions.length <= 1}
              >
                <SelectTrigger id="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map(option => (
                    <SelectItem key={option} value={option} className="capitalize">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {callerRole === 'manager' && (
                <p className="text-xs text-muted-foreground">
                  Managers can only invite members.
                </p>
              )}
            </div>

            {invite.error && (
              <p className="text-sm text-destructive">{invite.error.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={invite.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={invite.isPending || !email.trim()}>
              {invite.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
